import React, { useEffect, useState } from 'react'

import { Button, ModalBox, Toasts } from '@fourthwall/components'
import { ProductColorVariantProvider } from '@fourthwall/module-product'

import { useOfferMigrate } from '../../../../hooks/useOfferMigrateMutation/useOfferMigrateMutation'
import { useOfferMigrateProductOptionsQuery } from '../../../../hooks/useOfferMigrateProductOptionsQuery/useOfferMigrateProductOptionsQuery'
import * as S from './ChangeToAnotherProductModal.styled'
import { AnotherProductThumbnail } from './components/AnotherProductThumbnail/AnotherProductThumbnail'

type ProductOptions = NonNullable<
  ReturnType<typeof useOfferMigrateProductOptionsQuery>['offerMigrateProductOptionsQuery']['data']
>['migrationOptions']

export type ChangeToAnotherProductModalProps = {
  offerId: string
  onConfirm?: (productId: string) => void
  onClose?: () => void
}

export const ChangeToAnotherProductModal = ({
  offerId,
  onConfirm,
  onClose,
}: ChangeToAnotherProductModalProps) => {
  const [option, setOption] = useState<ProductOptions[number]>()

  const { offerMigrateMutation } = useOfferMigrate()
  const { offerMigrateProductOptionsQuery } = useOfferMigrateProductOptionsQuery([{ offerId }])

  const handleConfirm = async () => {
    if (!option) {
      throw new Error('Option is missing')
    }

    try {
      const { id } = await offerMigrateMutation.mutateAsync([
        { offerId },
        { productId: option.id, migrateToNewProduct: true },
      ])

      if (!id) {
        throw new Error('Product id is missing')
      }

      Toasts.notify('Product has been successfully migrated', {
        type: 'success',
      })

      onConfirm?.(id)
      onClose?.()
    } catch (error) {
      if (error instanceof Error) {
        Toasts.notify(error.message, {
          type: 'error',
        })
      }
    }
  }

  useEffect(() => {
    if (offerMigrateProductOptionsQuery.data) {
      if (offerMigrateProductOptionsQuery.data.migrationOptions.length === 0) {
        Toasts.notify(
          "You can't duplicate this product as it's either unavailable or out of stock at this time.",
          {
            type: 'error',
          },
        )
      } else {
        setOption(offerMigrateProductOptionsQuery.data.migrationOptions[0])
      }
    }
  }, [offerMigrateProductOptionsQuery.data])

  if (
    !offerMigrateProductOptionsQuery.data ||
    offerMigrateProductOptionsQuery.data.migrationOptions.length === 0
  ) {
    return null
  }

  return (
    <ModalBox
      size="large"
      title="This product can’t be duplicated"
      onClose={onClose}
      buttons={[
        <Button label="Cancel" appearance="secondary" onClick={onClose} />,
        <Button label="Confirm" appearance="primary" onClick={handleConfirm} />,
      ]}
    >
      <S.Lead>
        Product is not available anymore. However you may copy its design into a new product.
      </S.Lead>
      <S.Container>
        {offerMigrateProductOptionsQuery.data.migrationOptions.map((product) => (
          <ProductColorVariantProvider
            initialColorVariant={product.variants.matched[0]}
            colorVariants={product.variants.matched}
          >
            <AnotherProductThumbnail
              product={product}
              checked={option?.id === product.id}
              onChange={() => {
                setOption(product)
              }}
            />
          </ProductColorVariantProvider>
        ))}
      </S.Container>
    </ModalBox>
  )
}
